/**
 * Раздел «Настройки» — §7, §8.
 *
 * Реквизиты исполнителя, подпись и печать живут в «Документы → Реквизиты»:
 * их печатают документы, и искать их естественнее там.
 * SMTP видит только владелец, а пароль SMTP не отдаётся наружу вообще никому —
 * только признак того, задан он или нет.
 *
 * Тема — ЛИЧНАЯ: каждый выбирает её себе, и она едет за ним на любой
 * компьютер после входа. У Урсегова своя, у владельца своя (решение
 * владельца от 22.09.2026). Меняется сразу, без общей кнопки «Сохранить».
 *
 * Цвета статусов, сотрудников и «Финансов» — ОБЩИЕ, и править их может
 * только владелец: сотрудник этого раздела не видит вовсе.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Palette, Save, Settings as SettingsIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  darkerShade,
  employeeColors,
  formatDate,
  resolveFinanceColors,
  resolveStatusColors,
  resolveThemePair,
  textOnColor,
  type ThemeName,
  type ThemePair,
} from '@otc/shared';
import { api } from '../api/client.js';
import { queryKeys, useBootstrap, useMe, type CurrentUser } from '../api/queries.js';
import { useDrafts } from '../store/drafts.js';
import { useModule } from '../store/useModule.js';
import { Button, Field, SectionHeader, Spinner, Toast, useToast } from '../components/ui.js';
import { ВЕРСИЯ, ДАТА_СБОРКИ } from '../version.js';
import { SaveDiscard } from '../components/SaveDiscard.js';
import { InstallApp } from '../components/InstallApp.js';
import { ThemeTile } from '../components/ThemeTile.js';
import {
  ColorRow,
  ColorSettingsPanel,
  colorDraftChanges,
  colorDraftToSettings,
  type ColorDraft,
} from '../components/ColorSettingsPanel.js';
import { applyTheme, THEME_LIST } from '../theme.js';

export function Settings() {
  const me = useMe();
  const bootstrap = useBootstrap(true);
  const module = useModule('settings');
  const { toast, show } = useToast();

  const isOwner = me.data?.user.role === 'OWNER';
  const settings = bootstrap.data?.settings;

  /** Сотрудники с цветом: техники с зарплатой, владелец не в счёт. */
  const paid = useMemo(
    () =>
      (bootstrap.data?.employees ?? []).filter(
        (e) => e.role !== 'OWNER' && e.percent !== undefined,
      ),
    [bootstrap.data?.employees],
  );

  /** Сохранённые цвета, развёрнутые до полного набора. */
  const savedColors = useMemo<ColorDraft>(() => {
    const colors = settings?.colors;
    return {
      status: resolveStatusColors(colors),
      finance: resolveFinanceColors(colors),
      employees: Object.fromEntries(employeeColors(paid.map((e) => e.id), colors)),
    };
  }, [settings?.colors, paid]);

  /** Черновик цветов; `null` — ничего не трогали. */
  const [colorDraft, setColorDraft] = useState<ColorDraft | null>(null);
  const colorsChanged = colorDraft !== null && colorDraftChanges(colorDraft, savedColors) > 0;

  const save = async () => {
    if (!settings || !colorDraft) return;

    // Настройки — синглтон: черновик заводится под фиксированным id = 1.
    useDrafts.getState().load('settings', 'settings', [{ id: '1', version: settings.version }]);
    useDrafts.getState().update('settings', 'settings', '1', { colors: colorDraftToSettings(colorDraft) });

    const outcome = await module.save();
    if (outcome.ok) {
      setColorDraft(null);
      void bootstrap.refetch();
    } else {
      show(outcome.error ?? 'Не удалось сохранить', 'error');
    }
  };

  if (bootstrap.isLoading) return <Spinner />;

  return (
    /*
      Все блоки ОДНОЙ ширины. Цветам нужен образец рядом со строками, и раньше
      только их блок был шире — остальные обрывались на полпути, а кнопки
      «Сохранить» стояли над пустотой. Разнобой краёв читался как поломка.
    */
    <div className="max-w-6xl">
      {/* Та же шестерёнка, что у пункта «Настройки» в меню. */}
      <SectionHeader
        icon={SettingsIcon}
        title="Настройки"
        right={
          /*
            Общая кнопка — только для цветов: тема личная и сохраняется сама,
            а почта — своей кнопкой в своём блоке.
          */
          isOwner ? (
            <SaveDiscard
              dirty={colorsChanged}
              saving={module.saving}
              onSave={() => void save()}
              onDiscard={() => {
                module.discard();
                setColorDraft(null);
                show('Изменения отменены');
              }}
              what="Цвета"
              icon={<Save size={15} />}
            />
          ) : undefined
        }
      />

      {/* Установка приложения — первым блоком: этим пользуются раз, но ищут. */}
      <InstallApp />

      {me.data?.user && (
        <ThemeSection user={me.data.user} labTheme={settings?.themeName ?? 'teal'} onError={(text) => show(text, 'error')} />
      )}

      {/*
        Цвета — только владельцу, и не «только просмотр», а совсем: сотруднику
        нечего делать с кодами цветов, а раздел на полэкрана мешал бы найти
        то, что ему здесь действительно нужно.
      */}
      {isOwner && settings && (
        <ColorSettingsPanel
          draft={colorDraft ?? savedColors}
          saved={savedColors}
          employees={paid}
          onChange={setColorDraft}
        />
      )}

      {/* Почта — только владельцу */}
      {isOwner && (
        <MailSettings
          smtp={settings?.smtp ?? null}
          onDone={(text) => {
            /* Пустая весть — сохранение прошло молча. */
            if (text) show(text);
            void bootstrap.refetch();
          }}
          onFail={(text) => show(text, 'error')}
        />
      )}

      {/*
        ВЕРСИЯ — в самом низу настроек.

        Нужна для разговора по телефону: «какая у тебя версия» — вопрос,
        на который раньше нельзя было ответить, номер лежал только в
        исходниках. Дата сборки рядом отвечает на второй обычный вопрос —
        свежая программа или страница осталась от прошлой недели.

        Приглушённой строкой без заголовка: это справка, на неё смотрят
        раз в полгода, а место в настройках занимают вещи, которые правят.
      */}
      <p className="mt-8 text-center text-[11px] text-ink2">
        OrthoTech · версия {ВЕРСИЯ}
        {ДАТА_СБОРКИ && ` · сборка от ${formatDate(ДАТА_СБОРКИ)}`}
      </p>

      <Toast toast={toast} />
    </div>
  );
}

/**
 * «Оформление» — ЛИЧНАЯ тема.
 *
 * Сохраняется сразу, без общей кнопки: это не правка данных лаборатории, а
 * вкус того, кто сидит за экраном, и отменять тут нечего — не понравилось,
 * нажал другую. Своя тема сохраняется с небольшой задержкой, пока двигают
 * ползунок: иначе каждый шаг цвета уходил бы на сервер отдельным запросом.
 */
function ThemeSection({
  user,
  labTheme,
  onError,
}: {
  user: CurrentUser;
  /** Тема лаборатории — у того, кто своей ещё не выбирал. */
  labTheme: ThemeName;
  onError: (text: string) => void;
}) {
  const queryClient = useQueryClient();
  /*
   * Выбор отмечается СРАЗУ, не дожидаясь ответа сервера: иначе поля своей
   * темы появлялись с задержкой, и казалось, что щелчок не сработал.
   */
  const [chosen, setChosen] = useState<ThemeName | null>(null);
  const current: ThemeName = chosen ?? user.themeName ?? labTheme;
  const [pair, setPair] = useState<ThemePair>(() => user.themePair ?? resolveThemePair(current));
  const [saved, setSaved] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  // Своя пара с сервера (другое устройство, повторный вход) — в поля.
  useEffect(() => {
    if (user.themePair) setPair(user.themePair);
  }, [user.themePair?.accent, user.themePair?.accentDark]);

  const store = async (themeName: ThemeName, themePair?: ThemePair) => {
    try {
      const ответ = await api.put<{ user: CurrentUser }>('/api/me/theme', { themeName, themePair });
      // Вошедший обновляется на месте: тема не должна ждать перезагрузки.
      queryClient.setQueryData(queryKeys.me, ответ);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1500);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Тему сохранить не удалось');
    }
  };

  const pickReady = (name: ThemeName) => {
    setChosen(name);
    applyTheme(name);
    void store(name);
  };

  /** Своя: применяется сразу, сохраняется, когда цвет перестали двигать. */
  const changeCustom = (next: ThemePair) => {
    setChosen('custom');
    setPair(next);
    applyTheme('custom', next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void store('custom', next), 600);
  };

  return (
    <section
      className="mb-4 rounded-lg border p-3 md:mb-6 md:p-4"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      {/* Без описания под заголовком — по решению владельца. */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">Оформление</p>
        {saved && (
          <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--otc-sage)' }}>
            <Check size={13} /> Сохранено
          </span>
        )}
      </div>

      {/*
        На телефоне — ровная сетка в два столбца: плитки разной длины, и
        свободный перенос давал рваный край с одной плиткой в последней строке.
      */}
      <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap sm:gap-2">
        {THEME_LIST.map((theme) => (
          <ThemeTile
            key={theme.name}
            label={theme.label}
            selected={current === theme.name}
            accent={theme.accent}
            onClick={() => pickReady(theme.name)}
          />
        ))}
        <ThemeTile
          label="Своя"
          selected={current === 'custom'}
          accent={pair.accent}
          swatch={
            current === 'custom' || user.themePair ? (
              <span
                className="inline-block h-4 w-4 flex-shrink-0 rounded-full"
                style={{ background: `linear-gradient(135deg, ${pair.accent} 50%, ${pair.accentDark} 50%)` }}
              />
            ) : (
              <Palette size={16} className="flex-shrink-0 text-ink2" />
            )
          }
          onClick={() => {
            // Выбор плитки — сразу на сервер; задержка нужна только ползунку.
            setChosen('custom');
            applyTheme('custom', pair);
            void store('custom', pair);
          }}
        />
      </div>

      {current === 'custom' && <CustomTheme pair={pair} onChange={changeCustom} />}
    </section>
  );
}

/**
 * Своя тема: два цвета — основной и тёмный.
 *
 * Основной красит кнопки, шапки таблиц и выделение; тёмный — боковое меню и
 * строку заголовка установленного приложения. На обоих лежат БЕЛЫЕ надписи,
 * поэтому слишком светлый цвет честно подсвечивается: иначе меню можно
 * «перекрасить» так, что пунктов станет не прочесть.
 */
function CustomTheme({ pair, onChange }: { pair: ThemePair; onChange: (pair: ThemePair) => void }) {
  const [open, setOpen] = useState<string | null>(null);
  /* Точка возврата — пара, с которой открыли настройку. */
  const [start] = useState(pair);
  const светлый = (hex: string) => textOnColor(hex) !== '#FFFFFF';

  return (
    <div className="mt-3">
      <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--otc-line)' }}>
        <ColorRow
          id="theme.accent"
          name="Основной"
          where="кнопки, шапки таблиц, выделение"
          value={pair.accent}
          reference={start.accent}
          referenceLabel="прежний"
          open={open === 'theme.accent'}
          onOpen={setOpen}
          onChange={(accent) => onChange({ ...pair, accent })}
        />
        <ColorRow
          id="theme.accentDark"
          name="Тёмный"
          where="боковое меню, заголовок окна приложения"
          value={pair.accentDark}
          reference={start.accentDark}
          referenceLabel="прежний"
          open={open === 'theme.accentDark'}
          onOpen={setOpen}
          onChange={(accentDark) => onChange({ ...pair, accentDark })}
        />
      </div>

      <button
        type="button"
        onClick={() => onChange({ ...pair, accentDark: darkerShade(pair.accent) })}
        className="mt-2 rounded-lg border px-2.5 py-1.5 text-xs text-ink2 hover:bg-black/5"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        Подобрать тёмный по основному
      </button>

      {(светлый(pair.accent) || светлый(pair.accentDark)) && (
        <p
          className="mt-2 flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-xs leading-snug"
          style={{
            color: 'var(--otc-amber)',
            background: 'color-mix(in srgb, var(--otc-amber) 8%, var(--otc-surface))',
            borderColor: 'color-mix(in srgb, var(--otc-amber) 25%, transparent)',
          }}
        >
          <AlertTriangle size={14} className="mt-px flex-shrink-0" />
          {светлый(pair.accentDark)
            ? 'Тёмный слишком светлый: белые пункты бокового меню будут плохо видны.'
            : 'Основной слишком светлый: белые надписи на кнопках и в шапках таблиц будут плохо видны.'}{' '}
          Возьмите цвет темнее.
        </p>
      )}
    </div>
  );
}

/**
 * Реквизиты почты, с которой уходят счета и письма.
 *
 * Пароль набирается ЗДЕСЬ и уходит отдельным запросом, минуя общий механизм
 * черновиков: тот держит правки в памяти браузера до нажатия «Сохранить» и
 * пишет их в журнал изменений — пароль от ящика там не место.
 *
 * Обратно пароль не приходит никогда, поэтому поле всегда пустое, а под ним
 * написано, задан он или нет. Пустое поле при сохранении означает «оставить
 * прежний», иначе смена одного порта стирала бы пароль.
 */
function MailSettings({
  smtp,
  onDone,
  onFail,
}: {
  smtp: { host: string; port: string; login: string; hasPassword: boolean } | null;
  onDone: (text: string) => void;
  onFail: (text: string) => void;
}) {
  const [host, setHost] = useState(smtp?.host ?? '');
  /*
   * По умолчанию 465: шифрование там начинается с первого байта. На 587 оно
   * поднимается уже после приветствия открытым текстом — работает не хуже,
   * но требует, чтобы обе стороны договорились о переходе. Отправка различает
   * порты сама, так что менять его нужно, только если хостинг закрыл один из них.
   */
  const [port, setPort] = useState(smtp?.port ?? '465');
  const [login, setLogin] = useState(smtp?.login ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);

  /** Без сервера и логина настройка бессмысленна: слать некуда и не от кого. */
  const incomplete = host.trim() === '' || login.trim() === '';

  const save = async () => {
    setBusy('save');
    try {
      await api.post('/api/settings/smtp', {
        host,
        port,
        login,
        // Пустое поле — «не трогать сохранённый пароль».
        password: password === '' ? null : password,
      });
      setPassword('');
      onDone('');
    } catch (error) {
      onFail(error instanceof Error ? error.message : 'Не удалось сохранить');
    } finally {
      setBusy(null);
    }
  };

  /** Проверка идёт по СОХРАНЁННЫМ реквизитам — проверять надо то, чем шлём. */
  const test = async () => {
    setBusy('test');
    try {
      const result = await api.post<{ message?: string }>('/api/settings/smtp/test');
      onDone(result.message ?? 'Почта отвечает');
    } catch (error) {
      onFail(error instanceof Error ? error.message : 'Проверка не прошла');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="rounded-lg border p-3 md:p-4"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      {/* Без описания под заголовком — по решению владельца. */}
      <p className="mb-2 text-sm font-medium text-ink">Почта для отправки счетов и писем</p>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Сервер">
          <input
            className="otc-input"
            value={host}
            placeholder="smtp.mail.ru"
            onChange={(e) => setHost(e.target.value)}
          />
        </Field>
        <Field label="Порт" hint="465 или 587 — работают оба">
          <input
            className="otc-input tabular"
            inputMode="numeric"
            value={port}
            onChange={(e) => setPort(e.target.value)}
          />
        </Field>
        <Field label="Логин">
          <input
            className="otc-input"
            value={login}
            placeholder="orthotech.irk@mail.ru"
            onChange={(e) => setLogin(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Пароль приложения">
          <input
            className="otc-input"
            type="password"
            autoComplete="new-password"
            value={password}
            placeholder={smtp?.hasPassword ? 'задан — оставьте пустым, чтобы не менять' : 'не задан'}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      </div>

      {/*
        Сохранение заперто, пока не заполнены сервер и логин.

        Серый текст в полях — подсказка, а не значение, но с одного взгляда
        они выглядят заполненными. Так и вышло: сохранился один пароль, а
        слать оказалось некуда и не от кого, причём программа промолчала.
      */}
      {incomplete && (
        <p className="mt-3 text-xs" style={{ color: 'var(--otc-amber)' }}>
          Заполните сервер и логин — серый текст в полях только подсказывает, что вписать.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={busy !== null || incomplete}>
          {busy === 'save' ? 'Сохранение…' : 'Сохранить почту'}
        </Button>
        <Button variant="secondary" onClick={test} disabled={busy !== null}>
          {busy === 'test' ? 'Проверка…' : 'Проверить связь'}
        </Button>
        <span className="text-xs text-ink2">
          Пароль: {smtp?.hasPassword ? 'задан' : 'не задан'}
        </span>
      </div>
    </section>
  );
}
